import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageSquare, Send, Users, Link2, Image as ImageIcon, Share2 } from "lucide-react";
import { useNativeShare } from "@/hooks/useNativeShare";

/**
 * ReceiptShareModal — appears after an abono was registered and the
 * auto-receipt was successfully generated. Admin can send the receipt
 * directly to the client's number OR pick another WhatsApp contact.
 *
 * Props:
 *   receiptShare: { receipt_number, share_url (HTML preview URL with OG tags),
 *                   image_url (direct PNG), message_id, client_name,
 *                   client_phone, service_name, amount, currency,
 *                   fully_paid, pending_booking_plan? }
 *   onClose: () => void
 *   onFollowUpBooking: (plan) => void  // called when admin closes AND the
 *                                        installment liquidated the plan.
 *                                        Parent uses it to open the
 *                                        booking-link modal next.
 */
export const ReceiptShareModal = ({ receiptShare, onClose, onFollowUpBooking }) => {
  const { canShare, share } = useNativeShare();
  if (!receiptShare) return null;
  const rs = receiptShare;
  const digits = String(rs.client_phone || "").replace(/\D/g, "");
  const fmt = `${Number(rs.amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 0 })} ${rs.currency || "MXN"}`;
  // Payment clearing status sentence (only when the admin specified it)
  const reflTxt = rs.reflected === true
    ? " ✅ El pago ya está reflejado en mi cuenta."
    : rs.reflected === false
      ? " ⏳ El pago aún no se refleja en mi cuenta; te avisaré en cuanto aparezca."
      : "";
  // The message points to the HTML preview URL so WhatsApp fetches OG tags
  // and shows a rich preview card with the receipt thumbnail.
  const bodyText = encodeURIComponent(
    rs.fully_paid
      ? `Hola ${rs.client_name}, adjunto el recibo ${rs.receipt_number} del pago que liquida tu ${rs.service_name}. Total pagado: ${fmt}.${reflTxt} Puedes verlo aquí:\n${rs.share_url}\n\nBendiciones.`
      : `Hola ${rs.client_name}, adjunto el recibo ${rs.receipt_number} por tu abono de ${fmt} para ${rs.service_name}.${reflTxt} Puedes verlo aquí:\n${rs.share_url}\n\nBendiciones.`
  );
  // Plain-text version (no URL-encoding) for the native share picker
  const plainText = rs.fully_paid
    ? `Hola ${rs.client_name}, adjunto el recibo ${rs.receipt_number} del pago que liquida tu ${rs.service_name}. Total pagado: ${fmt}.${reflTxt}\n\nBendiciones.`
    : `Hola ${rs.client_name}, adjunto el recibo ${rs.receipt_number} por tu abono de ${fmt} para ${rs.service_name}.${reflTxt}\n\nBendiciones.`;
  const waDirect = digits ? `https://wa.me/${digits}?text=${bodyText}` : null;
  const waPickContact = `https://wa.me/?text=${bodyText}`;

  const nativeShare = async () => {
    const r = await share({
      url: rs.share_url,
      text: plainText,
      title: `Recibo ${rs.receipt_number} · CrisAngel Astral`,
      imageUrl: rs.image_url || rs.share_url,
    });
    if (r.ok) toast.success("Compartido");
  };

  const closeAndFollowUp = () => {
    const followUpPlan = rs.pending_booking_plan;
    onClose();
    if (followUpPlan && onFollowUpBooking) {
      // Small delay so React finishes unmounting the first modal cleanly
      setTimeout(() => onFollowUpBooking(followUpPlan), 150);
    }
  };

  const copyLink = async () => {
    // Try modern Clipboard API first, fall back to legacy execCommand.
    // On iOS webviews and non-secure origins the modern API is rejected.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(rs.share_url);
      } else {
        throw new Error("Clipboard API unavailable");
      }
      toast.success("Enlace copiado");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = rs.share_url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) toast.success("Enlace copiado");
        else toast.error("No se pudo copiar — cópialo manualmente del enlace de descarga");
      } catch {
        toast.error("No se pudo copiar el enlace en este navegador");
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[145] bg-black/85 backdrop-blur-md flex items-center justify-center px-3"
      data-testid="receipt-share-modal"
      onClick={closeAndFollowUp}
    >
      <div
        className="w-full max-w-md bg-gradient-to-br from-[#0a0510] to-black border-2 border-emerald-500/50 rounded-2xl p-5 shadow-[0_0_60px_rgba(16,185,129,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="w-5 h-5 text-emerald-300" />
          <h3 className="font-serif text-lg text-white">Enviar comprobante por WhatsApp</h3>
        </div>
        <p className="text-xs text-neutral-400 mb-4">
          Recibo <b className="text-white">{rs.receipt_number}</b> generado y enviado al chat del cliente.
          {rs.reflected === false && <span className="block mt-1 text-amber-300" data-testid="receipt-share-pending-note">⏳ Registrado como <b>pendiente de reflejarse</b> — podrás marcarlo como reflejado desde el historial del abono.</span>}
          {rs.reflected === true && <span className="block mt-1 text-emerald-300" data-testid="receipt-share-reflected-note">✅ Registrado como <b>reflejado en cuenta</b>.</span>}
          {" "}Comparte también por WhatsApp:
        </p>

        {/* Clickable preview → opens the HTML page (WhatsApp shows a rich card) */}
        <div className="mb-3 rounded-lg bg-black/40 border border-white/10 p-2.5 text-[11px]">
          <a
            href={rs.share_url}
            target="_blank"
            rel="noreferrer"
            data-testid="receipt-share-preview-open"
            className="block group relative"
            title="Abrir vista pública con miniatura para WhatsApp"
          >
            <img
              src={rs.image_url || rs.share_url}
              alt="Vista previa recibo"
              className="w-full max-h-64 object-contain rounded transition group-hover:opacity-90"
              data-testid="receipt-share-preview"
            />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/40 rounded">
              <span className="text-[10px] uppercase tracking-widest text-[#d4af37] bg-black/70 px-3 py-1 rounded-full">
                Abrir en tamaño completo
              </span>
            </div>
          </a>
        </div>

        <div className="space-y-2">
          {/* Native share — mobile-only. When available it opens the OS share
              sheet (WhatsApp, Telegram, Messages, AirDrop, Email…) in ONE tap.
              Shares the image as a File when the browser supports it → rich
              preview inside the messaging apps. */}
          {canShare && (
            <button
              data-testid="receipt-share-native"
              onClick={nativeShare}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#d4af37] hover:bg-[#f3d878] text-black font-bold text-xs uppercase tracking-widest"
              title="Abre el compartir del sistema (WhatsApp, Telegram, Messages, Email…)"
            >
              <Share2 className="w-3.5 h-3.5" />
              Compartir con app…
            </button>
          )}
          {waDirect && (
            <a
              data-testid="receipt-share-wa-client"
              href={waDirect}
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs uppercase tracking-widest"
            >
              <Send className="w-3.5 h-3.5" />
              Enviar a {rs.client_name} · {rs.client_phone}
            </a>
          )}
          {!waDirect && (
            <div className="w-full text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-center">
              Este cliente no tiene teléfono registrado — usa el selector de contacto abajo.
            </div>
          )}
          <a
            data-testid="receipt-share-wa-contact"
            href={waPickContact}
            target="_blank"
            rel="noreferrer"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-200 hover:bg-white/5 text-xs"
          >
            <Users className="w-3.5 h-3.5" />
            Elegir contacto de WhatsApp
          </a>
          <button
            data-testid="receipt-share-copy-link"
            onClick={copyLink}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-400 hover:text-white hover:bg-white/5 text-xs"
          >
            <Link2 className="w-3.5 h-3.5" /> Copiar enlace del recibo
          </button>
          <a
            data-testid="receipt-share-download"
            href={rs.image_url || rs.share_url}
            download={`recibo-${rs.receipt_number}.png`}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-white/15 text-neutral-400 hover:text-white hover:bg-white/5 text-xs"
          >
            <ImageIcon className="w-3.5 h-3.5" /> Descargar imagen
          </a>
        </div>

        <button
          data-testid="receipt-share-close"
          onClick={closeAndFollowUp}
          className="mt-4 w-full text-[10px] text-neutral-500 hover:text-white uppercase tracking-widest"
        >
          {rs.pending_booking_plan ? "Continuar al enlace de agendado" : "Cerrar"}
        </button>
      </div>
    </motion.div>
  );
};

export default ReceiptShareModal;
